// Store links drop loose utm_* params: Play needs everything percent-encoded
// into a single `referrer` wrapper, Apple reads pt/ct instead.

/** Apple Campaign provider token, shared by every GTAF app. */
export const GTAF_PROVIDER_TOKEN = '119135452';

/** Apple silently truncates campaign tokens past this length. */
export const CAMPAIGN_TOKEN_MAX_LENGTH = 30;

export const isPlayStoreUrl = (url) => /(^|\/\/)play\.google\.com\//.test(url);

export const isAppleStoreUrl = (url) =>
    /(^|\/\/)(apps|itunes)\.apple\.com\//.test(url);

/** The inner query that goes inside the Play Store `referrer` wrapper. */
export const buildInstallReferrer = (utmParams = {}, otherParams = {}) => {
    const params = new URLSearchParams();

    // Links arrive as `code` or `referral`; the apps only read back `code`.
    const code = otherParams.code || otherParams.referral;
    if (code) params.set('code', code);

    ['source', 'medium', 'campaign', 'content', 'term'].forEach((key) => {
        if (utmParams[key]) params.set(`utm_${key}`, utmParams[key]);
    });

    Object.entries(otherParams).forEach(([key, value]) => {
        if (key === 'code' || key === 'referral') return;
        if (value && !params.has(key)) params.set(key, value);
    });

    return params.toString();
};

export const sanitizeCampaignToken = (token) => {
    if (!token) return null;

    const trimmed = String(token).trim();
    if (!trimmed) return null;
    if (trimmed.length <= CAMPAIGN_TOKEN_MAX_LENGTH) return trimmed;

    if (process.env.NODE_ENV !== 'production') {
        console.warn(
            `Campaign token "${trimmed}" exceeds ${CAMPAIGN_TOKEN_MAX_LENGTH} characters and was truncated`
        );
    }

    return trimmed.slice(0, CAMPAIGN_TOKEN_MAX_LENGTH);
};

// Apple gives us one token, so ct carries medium and campaign together.
export const buildCampaignToken = (utmParams = {}) => {
    const { medium, campaign, source } = utmParams;

    const parts = [medium, campaign && campaign !== 'default' ? campaign : null].filter(
        Boolean
    );

    return sanitizeCampaignToken(parts.length ? parts.join('-') : source);
};

/** Returns null for non-store URLs, so callers keep their own handling. */
export const buildStoreUrl = (url, utmParams = {}, otherParams = {}) => {
    if (isPlayStoreUrl(url)) {
        const referrer = buildInstallReferrer(utmParams, otherParams);
        if (!referrer) return url;

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}referrer=${encodeURIComponent(referrer)}`;
    }

    if (isAppleStoreUrl(url)) {
        const campaignToken = buildCampaignToken(utmParams);
        if (!campaignToken) return url;

        const params = new URLSearchParams({
            pt: GTAF_PROVIDER_TOKEN,
            ct: campaignToken,
            mt: '8',
        });

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${params.toString()}`;
    }

    return null;
};
